import axios from "axios"
import { AuthTokenManager } from "../helpers/commonUtills"

// Local-env mode: leave baseURL empty so /api/* stays relative and CRA's
// setupProxy.js forwards to LOCAL_API_BASE. Setting it to the CloudHub URL
// makes axios fire absolute requests that bypass the proxy entirely.
const IS_LOCAL = process.env.REACT_APP_AUTH_MODE === "local"

const axiosInstance = axios.create({
  baseURL: IS_LOCAL ? "" : process.env.REACT_APP_EXPERIENCE_URL,
  timeout: 60000,
})

/**
 * Wait for JWT to become available (max 5s, polls every 100ms).
 * Handles the race where Auth0 getAccessTokenSilently hasn't resolved
 * yet when components fire API calls on mount.
 */
const waitForToken = (maxWaitMs = 5000, intervalMs = 100) =>
  new Promise((resolve, reject) => {
    const token = AuthTokenManager.get()
    if (token) return resolve(token)

    let elapsed = 0
    const timer = setInterval(() => {
      elapsed += intervalMs
      const t = AuthTokenManager.get()
      if (t) {
        clearInterval(timer)
        resolve(t)
      } else if (elapsed >= maxWaitMs) {
        clearInterval(timer)
        reject(new Error("JWT token not available after 5s"))
      }
    }, intervalMs)
  })

// Attach JWT to every request — waits for token if not immediately available
axiosInstance.interceptors.request.use(
  async (config) => {
    // Public KB routes are JWT-exempt by design (login page link probe,
    // login-help page). Skip waitForToken so the landing page doesn't
    // sit for 5s waiting for a token that will never exist.
    const url = config.url || ""
    if (url.startsWith("/api/public/") || url.includes("/api/public/")) {
      return config
    }
    try {
      const token = await waitForToken()
      config.headers.Authorization = `Bearer ${token}`
    } catch {
      // Token never arrived — let the request proceed without auth
      // (API will return 401, response interceptor handles it)
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Global 401 handling — only clear token for auth-specific 401s, not API 401s.
// API 401s ("Authentication denied", "Invalid Client") mean the request had bad/missing
// credentials but the JWT itself may still be valid. Clearing the token on every 401
// causes a cascade where one failed request wipes the token for all others.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only clear token if the 401 indicates the JWT itself is invalid/expired
    // (not when the API rejects for missing client credentials)
    if (error.response?.status === 401) {
      const errorMsg = error.response?.data?.error || ""
      const isTokenInvalid =
        errorMsg.includes("token") ||
        errorMsg.includes("expired") ||
        errorMsg.includes("invalid_token")
      if (isTokenInvalid) {
        AuthTokenManager.remove()
      }
    }
    return Promise.reject(error)
  },
)

export default axiosInstance
